import {Injectable} from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from "@angular/common/http";
import {UserSearchCriteria} from "../model/user.search.criteria";
import {Observable} from "rxjs";
import {UserDto} from "../model/user.dto";
import {WalletTransactionRequestDto} from "../model/wallet.transaction.request.dto";
import {UpdateWalletTransactionStatusRequestDto} from "../model/update.wallet.transaction.status.request.dto";
import {UserSchemePayoutModeDto} from "../model/user.scheme.payout.mode.dto";
import {GeneralConfigDto} from "../model/general-config.dto";
import {UserPersonalInformationDto} from "../model/user.personal.information.dto";
import {AddressDto} from "../model/address.dto";
import {LoginOtpVerificationResponseDto} from "../model/login.otp.verification.response.dto";
import {MessageRequestDto} from "../model/messageRequestDto";
import {AgentDetailsDto} from "../model/agent.details.dto";
import {OtpVerificationResponseDto} from "../model/otp.verification.response.dto";
import {KycRequestDto} from "../model/kyc.request.dto";
import {UserNomineeDto} from "../model/user.nominee.dto";
import {KycResponseDto} from "../model/kyc.response.dto";
import {KycStatusCheckDto} from "../model/kyc.status.check.dto";
import {KycStatusResponseDto} from "../model/kyc.status.response.dto";
import {CashToMetalConversionRequestDto} from "../model/cash.to.metal.conversion.request.dto";
import {AgentDetailsForSuperadminDto} from "../model/agent.details.for.superadmin.dto";
import {AgentClassificationRequestDto} from "../model/agent.classification.request.dto";
import {WalletDto} from "../model/wallet.dto";
import {UserLotteryTokensDto} from "../model/user.lottery.tokens.dto";
import {DeliveriesSearchCriteria} from "../model/deliveries.search.criteria";
import {AadhaarOtpSendResponse, AadhaarOtpVerifyRequest} from "../model/kyc.aadhar.otp.dto";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient) {
  }

  sendOtp(otpRequestDto: MessageRequestDto): Observable<HttpResponse<any>> {
    return this.http.post<MessageRequestDto>('/app/login/otp', otpRequestDto, {observe: "response"})
  }

  verifyOtp(authFactorValue: string, otp: string): Observable<HttpResponse<LoginOtpVerificationResponseDto>> {
    let params = new HttpParams().set("authFactorValue", authFactorValue).set("otp", otp);
    return this.http.post<LoginOtpVerificationResponseDto>('/app/login/verifyOtp', null, {
      observe: "response",
      params: params
    })
  }

  getUsers(userSearchCriteria: UserSearchCriteria): Observable<HttpResponse<UserDto[]>> {
    return this.http.post<UserDto[]>('/app/users/search', userSearchCriteria, {observe: "response"})
  }

  getUsersCount(userSearchCriteria: UserSearchCriteria): Observable<HttpResponse<number>> {
    return this.http.post<number>('/app/users/count', userSearchCriteria, {observe: "response"})
  }

  getUserById(userId: string): Observable<HttpResponse<UserDto>> {
    return this.http.get<UserDto>(`/app/users/${userId}`, {observe: "response"})
  }

  createUser(userDto: UserDto): Observable<HttpResponse<any>> {
    return this.http.post('/app/users', userDto, {observe: "response"})
  }

  updateUser(userId: string, userDto: UserDto): Observable<HttpResponse<any>> {
    return this.http.put(`/app/users/${userId}`, userDto, {observe: "response"})
  }

  panValidation(userId: string, userDto: UserDto): Observable<HttpResponse<any>> {
    return this.http.post(`/app/kyc/panFullDetails/${userId}`, userDto, {observe: "response"})
  }

  updateSchemePaymentMode(schemeId: string, userSchemePayoutModeDto: UserSchemePayoutModeDto, otp: string): Observable<HttpResponse<any>> {
    let params = new HttpParams().set("otp", otp);
    return this.http.put(`/app/user-schemes/${schemeId}/payoutMode`, userSchemePayoutModeDto, {observe: "response", params: params})
  }

  getWallet(userId: string): Observable<HttpResponse<WalletDto>> {
    return this.http.get<WalletDto>(`/app/users/${userId}/wallet`, {observe: "response"})
  }

  createWalletTransaction(walletId: string, walletTransactionRequestDto: WalletTransactionRequestDto, otp: string): Observable<HttpResponse<any>> {
    let params = new HttpParams().set("otp", otp);
    return this.http.post(`/app/wallets/${walletId}/transactions`, walletTransactionRequestDto, {
      observe: "response",
      params: params
    })
  }


  conversionOverview(walletId: string, cashToMetalConversionRequestDto: CashToMetalConversionRequestDto): Observable<HttpResponse<any>> {
    return this.http.post(`/app/wallets/${walletId}/transactions/conversionOverview`, cashToMetalConversionRequestDto, {observe: "response"})
  }


  updateWalletTransaction(walletId: string, transactionId: string, updateWalletTransactionRequestDto: UpdateWalletTransactionStatusRequestDto): Observable<HttpResponse<any>> {
    return this.http.patch(`/app/wallets/${walletId}/transactions/${transactionId}`, updateWalletTransactionRequestDto, {observe: "response"})
  }

  getProfileData(userId: string): Observable<HttpResponse<UserPersonalInformationDto>> {
    return this.http.get<UserPersonalInformationDto>(`/app/users/${userId}/personal-information`, {observe: "response"})
  }

  updateProfileData(userId: string, userPersonalInformationDto: UserPersonalInformationDto): Observable<HttpResponse<any>> {
    return this.http.put(`/app/users/${userId}/personal-information`, userPersonalInformationDto, {observe: "response"})
  }

  createAddress(userId: string, addressDto: AddressDto, otp: string): Observable<HttpResponse<OtpVerificationResponseDto>> {
    let params = new HttpParams().set("otp", otp);
    return this.http.post<OtpVerificationResponseDto>(`/app/users/${userId}/addresses`, addressDto, {
      observe: "response",
      params: params
    })
  }

  sendingOtp(userId: string, otpRequestDto: MessageRequestDto): Observable<HttpResponse<any>> {
    return this.http.post<MessageRequestDto>(`/app/users/${userId}/message`, otpRequestDto, {observe: "response"})
  }

  getAddressData(userId: string): Observable<HttpResponse<AddressDto>> {
    return this.http.get<AddressDto>(`/app/users/${userId}/addresses`, {observe: "response"})
  }

  getAddressById(userId: string, addressId: string): Observable<HttpResponse<AddressDto>> {
    return this.http.get<AddressDto>(`/app/users/${userId}/addresses/${addressId}`, {observe: "response"})
  }

  updateAddress(userId: string, addressId: string, addressDto: AddressDto, otp: string): Observable<HttpResponse<OtpVerificationResponseDto>> {
    let params = new HttpParams().set("otp", otp);
    return this.http.put<OtpVerificationResponseDto>(`/app/users/${userId}/addresses/${addressId}`, addressDto, {
      observe: "response",
      params: params
    })
  }

  updateAddressByInternalUser(userId: string, addressId: string, addressDto: AddressDto): Observable<HttpResponse<void>> {
    return this.http.put<void>(`/app/users/${userId}/addresses/${addressId}/admin-update`, addressDto, {
        observe: 'response'
      }
    );
  }


  getGeneralConfig(): Observable<HttpResponse<GeneralConfigDto>> {
    return this.http.get<GeneralConfigDto>(`/app/general-config`, {observe: "response"})
  }

  getAgentsCount(userSearchCriteria: UserSearchCriteria): Observable<HttpResponse<number>> {
    return this.http.post<number>('/app/agents/count', userSearchCriteria, {observe: "response"})
  }

  getAgents(userSearchCriteria: UserSearchCriteria): Observable<HttpResponse<AgentDetailsForSuperadminDto[]>> {
    return this.http.post<AgentDetailsForSuperadminDto[]>(`/app/agents/search`, userSearchCriteria, {observe: "response"})
  }

  getUpdateToAgent(userId: string) {
    return this.http.put(`/app/users/${userId}/agent-upgrade`, {observe: "response"})
  }

  getAgentById(agentId: string): Observable<HttpResponse<AgentDetailsDto>> {
    return this.http.get<AgentDetailsDto>(`/app/agents/${agentId}`, {observe: "response"})
  }

  updateAgentClassification(agentId: string, agentClassificationRequestDto: AgentClassificationRequestDto): Observable<HttpResponse<void>> {
    return this.http.put<void>(`/app/agents/${agentId}/classification`, agentClassificationRequestDto, {observe: "response"})
  }

  digiKycAadharInit(kycReqDto: KycRequestDto): Observable<HttpResponse<KycResponseDto>> {
    return this.http.post<KycResponseDto>('app/kyc/digilocker', kycReqDto, {observe: "response"})
  }

  digiKycAadharCheckStatus(statusCheckDto: KycStatusCheckDto): Observable<HttpResponse<KycStatusResponseDto>> {
    return this.http.post<KycStatusResponseDto>('app/kyc/digilocker/status', statusCheckDto, {observe: "response"})
  }

  getNomineeDetails(userId: string): Observable<UserNomineeDto> {
    return this.http.get<UserNomineeDto>(`/app/users/${userId}/nominee`);
  }

  addNomineeDetails(userNomineeDto: UserNomineeDto, userId: string): Observable<UserNomineeDto> {
    return this.http.post<UserNomineeDto>(`/app/users/${userId}/nominee`, userNomineeDto);
  }
  updateNominee(userId: string, nomineeId: string, nomineeDto: UserNomineeDto, otp: string): Observable<HttpResponse<OtpVerificationResponseDto>> {
    let params = new HttpParams().set("otp", otp);
    return this.http.put<OtpVerificationResponseDto>(`/app/users/${userId}/nominee/${nomineeId}`, nomineeDto, {
      observe: "response",
      params: params
    })
  }

  updateNomineeByUserManager(userId: string, nomineeId: string, nomineeDto: UserNomineeDto): Observable<HttpResponse<any>> {
    return this.http.put<OtpVerificationResponseDto>(`/app/users/${userId}/nomineeByAdmin/${nomineeId}`, nomineeDto, {
      observe: "response"
    })
  }

  deleteUser(userId: string): Observable<UserDto> {
    return this.http.delete<UserDto>(`/app/users/${userId}`)
  }

  getUnMaskAadhaar(userId: string):Observable<any>{
    return this.http.get(`/app/users/${userId}/unMaskAadhaar`);
  }

  getUnMaskPan(userId: string): Observable<any>{
    return this.http.get(`/app/users/${userId}/unMaskPan`);
  }

  getAgentUsers(agentId: string): Observable<HttpResponse<UserDto[]>> {
    return this.http.get<UserDto[]>(`/app/agents/${agentId}/getLinkedCustomers`,  {observe: "response"})
  }

  getActiveLotteryToken(userId: string): Observable<HttpResponse<UserLotteryTokensDto>> {
    return this.http.get<UserLotteryTokensDto>(`/app/users/${userId}/activeLotteryTokens`,  {observe: "response"})
  }

  getInternalApplicationRoles(): Observable<HttpResponse<string[]>> {
    return this.http.get<string[]>('/app/users/application-roles', {observe: "response"});
  }

  exportUsers(criteria: UserSearchCriteria): Observable<Blob> {
    return this.http.post('/app/users/export', criteria, { responseType: 'blob' });
  }

  getActiveKycApi(): Observable<'CASHFREE' | 'DIGITAP'> {
    return this.http.get(`/app/users/active-kyc-api`, { responseType: 'text' }) as Observable<'CASHFREE' | 'DIGITAP'>;
  }

  sendAadhaarOtp(userId: string) {
    return this.http.post<AadhaarOtpSendResponse>(`/app/kyc/aadhaar/otp/${userId}`, {}, {observe: 'response'});
  }

  verifyAadhaarOtp(payload: AadhaarOtpVerifyRequest) {
    return this.http.post<KycStatusResponseDto>(`/app/kyc/aadhaar/otp/verify`, payload, {observe: 'response'});
  }
}
